<div [class.im-page]="!tvMode" [class.im-tv]="tvMode">

  <!-- ═══════════════════════════════════════════════════════════════ -->
  <!-- TV MODE HEADER                                                  -->
  <!-- ═══════════════════════════════════════════════════════════════ -->
  <div *ngIf="tvMode" class="im-tv-header">
    <div class="d-flex align-items-center gap-3">
      <img src="/assets/customers/default/konti-small.png" alt="Konti" class="im-tv-logo">
      <h2 class="mb-0 fw-bold">{{ 'IntegrationMonitor.title' | translate }}</h2>
    </div>
    <div class="d-flex align-items-center gap-3">
      <!-- Countdown ring -->
      <div class="im-tv-countdown" [attr.aria-label]="('IntegrationMonitor.tvCountdown' | translate) + ': ' + countdownSeconds + 's'">
        <svg width="44" height="44" viewBox="0 0 44 44" aria-hidden="true">
          <circle cx="22" cy="22" r="18" stroke="#2d3038" stroke-width="3" fill="none"/>
          <circle cx="22" cy="22" r="18" stroke="#75d4a7" stroke-width="3" fill="none"
                  stroke-linecap="round"
                  [attr.stroke-dasharray]="113"
                  [attr.stroke-dashoffset]="113 - countdownDashOffset"
                  transform="rotate(-90 22 22)"/>
          <text x="22" y="26" text-anchor="middle" font-size="11" font-weight="700" fill="#e8e8e8">{{ countdownSeconds }}</text>
        </svg>
      </div>
      <span *ngIf="lastRefreshed" class="text-white-50 fs-6">
        {{ lastRefreshed | date:'HH:mm:ss' }}
      </span>
      <div *ngIf="loading" class="spinner-border spinner-border-sm text-info" role="status"></div>
    </div>
  </div>

  <!-- ═══════════════════════════════════════════════════════════════ -->
  <!-- ADMIN MODE HEADER                                               -->
  <!-- ═══════════════════════════════════════════════════════════════ -->
  <div *ngIf="!tvMode" class="im-admin-header dash-page-header">
    <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
      <h1 class="dash-page-title">
        <i class="bi bi-activity me-2" aria-hidden="true"></i>{{ 'IntegrationMonitor.title' | translate }}
      </h1>
      <div class="d-flex align-items-center gap-2">
        <small *ngIf="lastRefreshed" class="text-muted d-none d-sm-inline">
          {{ 'IntegrationMonitor.lastRefreshed' | translate }}: {{ lastRefreshed | date:'HH:mm:ss' }}
        </small>
        <a class="btn btn-sm btn-action-outline"
           [routerLink]="['/system-admin/integration-monitor']"
           [queryParams]="{tv: '1'}"
           target="_blank" rel="noopener"
           [attr.title]="'IntegrationMonitor.openTv' | translate"
           [attr.aria-label]="'IntegrationMonitor.openTv' | translate">
          <i class="bi bi-tv" aria-hidden="true"></i>
          <span class="d-none d-md-inline ms-1">{{ 'IntegrationMonitor.openTv' | translate }}</span>
        </a>
        <button type="button" class="btn btn-sm btn-action-outline"
                (click)="exportCsv()" [disabled]="!monitorData"
                [attr.title]="'IntegrationMonitor.actionsExportCsv' | translate"
                [attr.aria-label]="'IntegrationMonitor.actionsExportCsv' | translate">
          <i class="bi bi-download" aria-hidden="true"></i>
          <span class="d-none d-md-inline ms-1">{{ 'IntegrationMonitor.actionsExportCsv' | translate }}</span>
        </button>
        <button type="button" class="btn btn-sm btn-action-dark"
                (click)="loadData()" [disabled]="loading"
                [attr.aria-label]="'IntegrationMonitor.refresh' | translate">
          <i class="bi bi-arrow-clockwise" [class.im-spin]="loading" aria-hidden="true"></i>
          <span class="d-none d-sm-inline ms-1">{{ 'IntegrationMonitor.refresh' | translate }}</span>
        </button>
      </div>
    </div>
  </div>

  <!-- ═══════════════════════════════════════════════════════════════ -->
  <!-- LOADING / ERROR STATES                                          -->
  <!-- ═══════════════════════════════════════════════════════════════ -->
  <div *ngIf="loading && !monitorData" class="text-center py-5">
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">{{ 'General.loading' | translate }}</span>
    </div>
  </div>

  <div *ngIf="errorMessage && !loading" class="text-center py-5">
    <div class="alert alert-danger d-inline-block px-4 py-3">
      <i class="bi bi-exclamation-triangle-fill me-2" aria-hidden="true"></i>
      <strong>{{ errorMessage }}</strong>
      <div class="mt-2" *ngIf="tvMode">
        <small class="text-white-50">{{ 'IntegrationMonitor.autoRetry' | translate }}</small>
      </div>
    </div>
  </div>

  <!-- ═══════════════════════════════════════════════════════════════ -->
  <!-- ADMIN MODE BODY                                                 -->
  <!-- ═══════════════════════════════════════════════════════════════ -->
  <ng-container *ngIf="monitorData && !tvMode">

    <!-- ── Status hero ─────────────────────────────────────────────── -->
    <div class="im-hero" [attr.data-state]="heroState">
      <div class="im-hero-icon">
        <i class="bi" aria-hidden="true"
           [class.bi-check-circle-fill]="heroState === 'ok'"
           [class.bi-exclamation-triangle-fill]="heroState === 'warning'"
           [class.bi-x-octagon-fill]="heroState === 'critical'"></i>
      </div>
      <div class="im-hero-content">
        <ng-container *ngIf="heroState === 'ok'">
          <div class="im-hero-title">{{ 'IntegrationMonitor.heroAllOk' | translate }}</div>
          <div class="im-hero-sub">{{ 'IntegrationMonitor.heroAllOkDesc' | translate }}</div>
        </ng-container>
        <ng-container *ngIf="heroState !== 'ok'">
          <div class="im-hero-title">
            <ng-container *ngIf="effectiveErrorTenantCount > 0">
              <strong>{{ effectiveErrorTenantCount }}</strong>
              {{ effectiveErrorTenantCount === 1 ? 'kunde' : 'kunder' }} {{ effectiveErrorTenantCount === 1 ? ('IntegrationMonitor.heroCritical' | translate) : ('IntegrationMonitor.heroCriticalPlural' | translate) }}
            </ng-container>
            <ng-container *ngIf="effectiveErrorTenantCount === 0 && effectiveWarningCount > 0">
              <strong>{{ effectiveWarningCount }}</strong>
              {{ effectiveWarningCount === 1 ? ('IntegrationMonitor.heroWarning' | translate) : ('IntegrationMonitor.heroWarningPlural' | translate) }}
            </ng-container>
            <ng-container *ngIf="effectiveErrorTenantCount === 0 && effectiveWarningCount === 0 && (summary!.neverExecuted || 0) > 0">
              <strong>{{ summary!.neverExecuted }}</strong> {{ 'IntegrationMonitor.kpiNeverRun' | translate | lowercase }}
            </ng-container>
          </div>
          <div class="im-hero-sub">
            <span *ngIf="(summary!.failedLast24h ?? 0) > 0">
              {{ summary!.failedLast24h }} {{ 'IntegrationMonitor.failedLast24h' | translate | lowercase }}
            </span>
            <span *ngIf="hasNewSummary && failedTrend !== 'flat' && summary!.failedPrevious24h > 0" class="ms-2">
              <i class="bi" [class.bi-arrow-up-right]="failedTrend === 'up'" [class.bi-arrow-down-right]="failedTrend === 'down'" aria-hidden="true"></i>
              {{ failedTrendDelta > 0 ? '+' : '' }}{{ failedTrendDelta }} {{ 'IntegrationMonitor.heroBetter' | translate }}
            </span>
            <span *ngIf="hasNewSummary && failedTrend === 'flat' && summary!.failedPrevious24h > 0" class="ms-2 text-muted">
              <i class="bi bi-dash" aria-hidden="true"></i> {{ 'IntegrationMonitor.heroNoChange' | translate }}
            </span>
            <span *ngIf="(summary!.failedLast24h ?? 0) === 0 && effectiveErrorTenantCount > 0" class="text-muted">
              {{ 'IntegrationMonitor.kpiActiveErrors' | translate | lowercase }} (eldre enn 24 t)
            </span>
            <span *ngIf="effectiveErrorTenantCount === 0 && (summary!.neverExecuted ?? 0) > 0" class="text-muted">
              {{ summary!.neverExecuted }} {{ 'IntegrationMonitor.kpiNeverRun' | translate | lowercase }}
            </span>
          </div>
        </ng-container>
      </div>
      <div class="im-hero-actions">
        <button *ngIf="newErrorCount > 0" type="button" class="btn btn-sm btn-warning im-hero-new"
                (click)="markAsSeen()">
          <i class="bi bi-bell-fill me-1" aria-hidden="true"></i>
          {{ 'IntegrationMonitor.heroNewSinceLastSeen' | translate:{count: newErrorCount} }}
        </button>
        <small *ngIf="lastSeenAt && newErrorCount === 0" class="text-muted">
          {{ 'IntegrationMonitor.heroSeenAgo' | translate:{ago: lastSeenAgo} }}
        </small>
      </div>
    </div>

    <!-- ── KPI cards (status only — not filters) ───────────────────── -->
    <div class="row g-2 mb-3">
      <div class="col-6 col-md-3 col-xl">
        <div class="dash-kpi-card dash-kpi-card--clickable dash-kpi-card--compact"
             role="button" tabindex="0"
             [class.im-kpi-active]="filterMode === 'errors'"
             (click)="setFilter('errors')" (keyup.enter)="setFilter('errors')" (keyup.space)="setFilter('errors')"
             [attr.aria-pressed]="filterMode === 'errors'">
          <div class="dash-kpi-icon dash-kpi-icon--danger">
            <i class="bi bi-x-octagon-fill" aria-hidden="true"></i>
          </div>
          <div class="dash-kpi-content">
            <div class="dash-kpi-value">{{ effectiveErrorTenantCount }}</div>
            <div class="dash-kpi-label">{{ 'IntegrationMonitor.kpiActiveErrors' | translate }}</div>
            <small *ngIf="hasNewSummary && (summary!.failedPrevious24h > 0 || summary!.failedLast24h > 0)" class="dash-kpi-delta"
                   [class.text-danger]="failedTrend === 'up'"
                   [class.text-success]="failedTrend === 'down'"
                   [class.text-muted]="failedTrend === 'flat'">
              <ng-container [ngSwitch]="failedTrend">
                <ng-container *ngSwitchCase="'up'">▲ +{{ failedTrendDelta }}</ng-container>
                <ng-container *ngSwitchCase="'down'">▼ {{ failedTrendDelta }}</ng-container>
                <ng-container *ngSwitchCase="'flat'">= {{ 'IntegrationMonitor.heroNoChange' | translate }}</ng-container>
              </ng-container>
            </small>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3 col-xl">
        <div class="dash-kpi-card dash-kpi-card--clickable dash-kpi-card--compact"
             role="button" tabindex="0"
             [class.im-kpi-active]="filterMode === 'warnings'"
             (click)="setFilter('warnings')" (keyup.enter)="setFilter('warnings')" (keyup.space)="setFilter('warnings')"
             [attr.aria-pressed]="filterMode === 'warnings'">
          <div class="dash-kpi-icon dash-kpi-icon--warning">
            <i class="bi bi-exclamation-triangle-fill" aria-hidden="true"></i>
          </div>
          <div class="dash-kpi-content">
            <div class="dash-kpi-value">{{ effectiveWarningCount }}</div>
            <div class="dash-kpi-label">{{ 'IntegrationMonitor.heroWarningPlural' | translate }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3 col-xl">
        <div class="dash-kpi-card dash-kpi-card--clickable dash-kpi-card--compact"
             role="button" tabindex="0"
             [class.im-kpi-active]="filterMode === 'neverrun'"
             (click)="setFilter('neverrun')" (keyup.enter)="setFilter('neverrun')" (keyup.space)="setFilter('neverrun')"
             [attr.aria-pressed]="filterMode === 'neverrun'">
          <div class="dash-kpi-icon dash-kpi-icon--overdue">
            <i class="bi bi-clock-history" aria-hidden="true"></i>
          </div>
          <div class="dash-kpi-content">
            <div class="dash-kpi-value">{{ summary!.neverExecuted }}</div>
            <div class="dash-kpi-label">{{ 'IntegrationMonitor.kpiNeverRun' | translate }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3 col-xl">
        <div class="dash-kpi-card dash-kpi-card--clickable dash-kpi-card--compact"
             role="button" tabindex="0"
             [class.im-kpi-active]="filterMode === 'connection'"
             (click)="setFilter('connection')" (keyup.enter)="setFilter('connection')" (keyup.space)="setFilter('connection')"
             [attr.aria-pressed]="filterMode === 'connection'">
          <div class="dash-kpi-icon dash-kpi-icon--info">
            <i class="bi bi-database-x" aria-hidden="true"></i>
          </div>
          <div class="dash-kpi-content">
            <div class="dash-kpi-value">{{ connectionErrorCount }}</div>
            <div class="dash-kpi-label">{{ 'IntegrationMonitor.kpiConnectionError' | translate }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3 col-xl">
        <div class="dash-kpi-card dash-kpi-card--clickable dash-kpi-card--compact"
             role="button" tabindex="0"
             [class.im-kpi-active]="filterMode === 'muted'"
             (click)="setFilter('muted')" (keyup.enter)="setFilter('muted')" (keyup.space)="setFilter('muted')"
             [attr.aria-pressed]="filterMode === 'muted'">
          <div class="dash-kpi-icon dash-kpi-icon--primary">
            <i class="bi bi-bell-slash" aria-hidden="true"></i>
          </div>
          <div class="dash-kpi-content">
            <div class="dash-kpi-value">{{ effectiveMutedCount }}</div>
            <div class="dash-kpi-label">{{ 'IntegrationMonitor.kpiMuted' | translate }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Filter bar ──────────────────────────────────────────────── -->
    <div class="d-flex align-items-center gap-2 mb-2 flex-wrap">

      <!-- Search -->
      <div class="im-search position-relative">
        <i class="bi bi-search im-search-icon" aria-hidden="true"></i>
        <input id="imSearchInput" type="search" class="form-control form-control-sm im-search-input"
               [(ngModel)]="searchText"
               [attr.placeholder]="'IntegrationMonitor.search' | translate"
               [attr.aria-label]="'IntegrationMonitor.search' | translate" />
        <kbd class="im-search-kbd d-none d-md-inline" aria-hidden="true">Ctrl K</kbd>
      </div>

      <!-- Filter toggle -->
      <button type="button" class="btn btn-sm position-relative"
              [ngClass]="showFilterPanel ? 'btn-dark' : (activeFilterCount > 0 ? 'btn-outline-primary' : 'btn-outline-secondary')"
              (click)="toggleFilterPanel()"
              [attr.aria-expanded]="showFilterPanel"
              [attr.aria-controls]="'imFilterPanel'">
        <i class="bi bi-funnel me-1" aria-hidden="true"></i>
        <span class="d-none d-md-inline">{{ 'IntegrationMonitor.filter' | translate }}</span>
        <span class="filter-count-badge" *ngIf="activeFilterCount > 0">{{ activeFilterCount }}</span>
      </button>

      <!-- Group toggle -->
      <div class="btn-group btn-group-sm" role="group" [attr.aria-label]="'IntegrationMonitor.groupByCustomer' | translate">
        <button type="button" class="btn"
                [class.btn-dark]="groupBy === 'customer'"
                [class.btn-outline-secondary]="groupBy !== 'customer'"
                (click)="setGroupBy('customer')"
                [attr.aria-pressed]="groupBy === 'customer'"
                [attr.title]="'IntegrationMonitor.groupByCustomer' | translate">
          <i class="bi bi-building" aria-hidden="true"></i>
          <span class="d-none d-lg-inline ms-1">{{ 'IntegrationMonitor.groupByCustomer' | translate }}</span>
        </button>
        <button type="button" class="btn"
                [class.btn-dark]="groupBy === 'type'"
                [class.btn-outline-secondary]="groupBy !== 'type'"
                (click)="setGroupBy('type')"
                [attr.aria-pressed]="groupBy === 'type'"
                [attr.title]="'IntegrationMonitor.groupByType' | translate">
          <i class="bi bi-collection" aria-hidden="true"></i>
          <span class="d-none d-lg-inline ms-1">{{ 'IntegrationMonitor.groupByType' | translate }}</span>
        </button>
      </div>

      <!-- Clear -->
      <button *ngIf="hasActiveFilters" type="button" class="btn btn-sm btn-link text-danger p-0 ms-1" (click)="clearFilters()">
        <i class="bi bi-x-circle me-1" aria-hidden="true"></i>
        <span class="d-none d-md-inline">{{ 'IntegrationMonitor.filterClear' | translate }}</span>
      </button>

      <!-- Counter -->
      <small class="text-muted ms-auto" *ngIf="filteredTenants.length > 0">
        {{ filteredTenants.length }} / {{ summary!.totalTenants }}
      </small>
    </div>

    <!-- Collapsible filter panel -->
    <div *ngIf="showFilterPanel" class="filter-panel mb-3" id="imFilterPanel">
      <div class="filter-panel-content">
        <div class="row g-2 align-items-end">
          <div class="col-md-4">
            <label class="form-label form-label-sm mb-1">{{ 'IntegrationMonitor.filterIntegrationType' | translate }}</label>
            <select class="form-select form-select-sm" [(ngModel)]="filterType">
              <option value="">{{ 'IntegrationMonitor.filterAllTypes' | translate }}</option>
              <option *ngFor="let t of availableTypes" [value]="t">{{ t }}</option>
            </select>
          </div>
          <div class="col-md-4">
            <label class="form-label form-label-sm mb-1">{{ 'IntegrationMonitor.filterSeverity' | translate }}</label>
            <select class="form-select form-select-sm" [(ngModel)]="filterSeverity">
              <option value="">{{ 'IntegrationMonitor.filterAllSeverities' | translate }}</option>
              <option value="P1">{{ 'IntegrationMonitor.severity.P1' | translate }}</option>
              <option value="P2">{{ 'IntegrationMonitor.severity.P2' | translate }}</option>
              <option value="P3">{{ 'IntegrationMonitor.severity.P3' | translate }}</option>
            </select>
          </div>
          <div class="col-md-3">
            <div class="form-check form-switch mt-md-4">
              <input class="form-check-input" type="checkbox" id="imShowAcked"
                     [checked]="showAcked" (change)="setShowAcked($any($event.target).checked)" />
              <label class="form-check-label form-label-sm" for="imShowAcked">
                {{ 'IntegrationMonitor.filterShowAcked' | translate }}
              </label>
            </div>
          </div>
          <div class="col-auto ms-auto">
            <button type="button" class="btn btn-sm btn-outline-danger" [disabled]="!hasActiveFilters" (click)="clearFilters()">
              <i class="bi bi-x-circle me-1" aria-hidden="true"></i>{{ 'IntegrationMonitor.filterClear' | translate }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Two-column body ─────────────────────────────────────────── -->
    <div class="row g-3 align-items-start">

      <!-- LEFT: Tenant cards -->
      <div class="col-12 col-lg-8">

        <!-- No results -->
        <div *ngIf="filteredTenants.length === 0" class="dash-card mb-3">
          <div class="dash-card-body text-center py-4">
            <ng-container *ngIf="filterMode === 'errors'">
              <i class="bi bi-check-circle-fill text-success d-block mb-2 dash-empty-state-icon" aria-hidden="true"></i>
              <p class="mb-0">{{ 'IntegrationMonitor.allOk' | translate }}</p>
            </ng-container>
            <ng-container *ngIf="filterMode !== 'errors'">
              <i class="bi bi-funnel d-block mb-2 dash-empty-state-icon text-muted" aria-hidden="true"></i>
              <p class="mb-0 text-muted">{{ 'IntegrationMonitor.noResultsForFilter' | translate }}</p>
            </ng-container>
          </div>
        </div>

        <!-- ─ Group by customer ─ -->
        <ng-container *ngIf="groupBy === 'customer'">

          <!-- Errors section -->
          <div *ngIf="errorTenants.length > 0" class="mb-3">
            <div class="dash-section-label im-section-danger">
              <i class="bi bi-exclamation-triangle-fill me-2" aria-hidden="true"></i>
              {{ 'IntegrationMonitor.needsAction' | translate }}
              <span class="badge badge-soft-danger ms-2">{{ errorTenants.length }}</span>
            </div>
            <div class="im-card-grid">
              <ng-container *ngFor="let tenant of errorTenants; trackBy: trackByDomain">
                <ng-container *ngTemplateOutlet="tenantCardTpl; context: { $implicit: tenant }"></ng-container>
              </ng-container>
            </div>
          </div>

          <!-- OK section -->
          <div *ngIf="okTenants.length > 0">
            <div class="dash-section-label im-section-success">
              <i class="bi bi-check-circle me-2" aria-hidden="true"></i>
              {{ 'IntegrationMonitor.tenantsOk' | translate }}
              <span class="badge badge-soft-success ms-2">{{ okTenants.length }}</span>
            </div>
            <div class="im-card-grid im-card-grid-compact">
              <ng-container *ngFor="let tenant of displayedOkTenants; trackBy: trackByDomain">
                <ng-container *ngTemplateOutlet="tenantCardTpl; context: { $implicit: tenant }"></ng-container>
              </ng-container>
            </div>
            <div *ngIf="okTenants.length > okDisplayLimit" class="text-center mt-3 mb-2">
              <button type="button" class="btn btn-sm btn-outline-secondary" (click)="showMoreOkTenants()">
                <i class="bi bi-chevron-down me-1" aria-hidden="true"></i>
                {{ 'IntegrationMonitor.showMore' | translate }} ({{ okTenants.length - okDisplayLimit }} {{ 'IntegrationMonitor.remaining' | translate }})
              </button>
            </div>
          </div>
        </ng-container>

        <!-- ─ Group by type ─ -->
        <ng-container *ngIf="groupBy === 'type'">
          <div *ngFor="let group of tenantsByType; trackBy: trackByType" class="mb-3">
            <div class="dash-section-label">
              <i class="bi bi-collection me-2" aria-hidden="true"></i>
              {{ group.type }}
              <span class="badge badge-soft-dark ms-2">{{ group.integrations.length }}</span>
            </div>
            <div class="dash-card mb-0">
              <div class="dash-card-body p-0">
                <ng-container *ngFor="let entry of group.integrations">
                  <ng-container *ngTemplateOutlet="integrationRowTpl; context: { tenant: entry.tenant, item: entry.item }"></ng-container>
                </ng-container>
              </div>
            </div>
          </div>
        </ng-container>

      </div>

      <!-- RIGHT: Live error feed + API logs -->
      <div class="col-12 col-lg-4 d-flex flex-column gap-3">

        <!-- Live error feed -->
        <div class="dash-card mb-0" *ngIf="getRecentErrors().length > 0">
          <div class="dash-card-header">
            <i class="bi bi-lightning-charge-fill me-2 text-danger" aria-hidden="true"></i>
            <span>{{ 'IntegrationMonitor.recentErrors' | translate }}</span>
            <span class="badge badge-soft-danger ms-auto">{{ getRecentErrors().length }}</span>
          </div>
          <div class="dash-card-body p-0 im-feed-scroll">
            <a *ngFor="let err of getRecentErrors(); trackBy: trackByErrorKey"
               class="list-group-item list-group-item-action d-flex flex-column gap-1"
               [class.im-feed-new]="isErrorNew(err.domainId, err.integrationId, err.isConnection)"
               (click)="openInLogs(err.tenantName, err.integrationName)">
              <div class="d-flex align-items-center gap-2">
                <strong class="text-truncate small">{{ err.tenantName }}</strong>
                <span *ngIf="err.alertLevel" class="badge badge-sm" [ngClass]="getSeverityClass(err.alertLevel)">{{ err.alertLevel }}</span>
                <small class="text-muted ms-auto" *ngIf="err.timeSince">{{ err.timeSince }}</small>
              </div>
              <div class="text-danger small">
                <i class="bi" [class.bi-database-x]="err.isConnection" [class.bi-x-circle]="!err.isConnection" aria-hidden="true"></i>
                {{ err.integrationName }}
                <small class="text-muted">{{ err.typeName }}</small>
              </div>
              <small *ngIf="err.errorMessage" class="text-muted text-truncate" [title]="err.errorMessage">
                {{ err.errorMessage }}
              </small>
            </a>
          </div>
        </div>

        <!-- API Error Log panel -->
        <div class="dash-card mb-0">
          <div class="dash-card-header">
            <i class="bi bi-journal-code me-2 text-warning" aria-hidden="true"></i>
            <span>{{ 'ApiErrorLog.recentApiLogs' | translate }}</span>
            <span *ngIf="recentApiLogs.length > 0" class="badge badge-soft-warning ms-auto">{{ recentApiLogs.length }}</span>
            <div *ngIf="loadingApiLogs" class="spinner-border spinner-border-sm text-secondary ms-auto" role="status"></div>
          </div>
          <div class="dash-card-body p-0 im-feed-scroll" *ngIf="recentApiLogs.length > 0">
            <a *ngFor="let log of recentApiLogs; trackBy: trackByApiLog"
               class="list-group-item list-group-item-action d-flex flex-column gap-1"
               [routerLink]="'/system-admin/api-error-log'"
               [queryParams]="{search: log.message?.substring(0, 30)}"
               [title]="log.message || ''">
              <div class="d-flex align-items-center gap-2">
                <span class="badge" [ngClass]="getApiLogLevelClass(log.level)">{{ log.level }}</span>
                <small class="text-muted ms-auto">{{ formatApiLogTime(log.timeStamp) }}</small>
              </div>
              <small class="text-muted text-truncate">{{ truncateApiLogMessage(log.message) }}</small>
            </a>
          </div>
          <div *ngIf="!loadingApiLogs && recentApiLogs.length === 0" class="dash-card-body text-center py-3 text-muted">
            <i class="bi bi-check-circle me-1" aria-hidden="true"></i>{{ 'ApiErrorLog.noRecentLogs' | translate }}
          </div>
          <div class="dash-card-footer">
            <a routerLink="/system-admin/api-error-log" class="btn btn-sm btn-action-outline">
              {{ 'ApiErrorLog.viewAll' | translate }} <i class="bi bi-arrow-right ms-1" aria-hidden="true"></i>
            </a>
          </div>
        </div>

        <!-- ePortal API Log panel -->
        <div class="dash-card mb-0">
          <div class="dash-card-header">
            <i class="bi bi-journal-text me-2 text-info" aria-hidden="true"></i>
            <span>{{ 'EPortalApiLog.recentLogs' | translate }}</span>
            <span *ngIf="recentEPortalLogs.length > 0" class="badge badge-soft-info ms-auto">{{ recentEPortalLogs.length }}</span>
            <div *ngIf="loadingEPortalLogs" class="spinner-border spinner-border-sm text-secondary ms-auto" role="status"></div>
          </div>
          <div class="dash-card-body p-0 im-feed-scroll" *ngIf="recentEPortalLogs.length > 0">
            <a *ngFor="let log of recentEPortalLogs; trackBy: trackByApiLog"
               class="list-group-item list-group-item-action d-flex flex-column gap-1"
               [routerLink]="'/system-admin/eportal-api-log'"
               [queryParams]="{domain: log.tenantDomain, search: log.message?.substring(0, 30)}"
               [title]="log.message || ''">
              <div class="d-flex align-items-center gap-2">
                <span class="badge" [ngClass]="getApiLogLevelClass(log.level)">{{ log.level }}</span>
                <small *ngIf="log.tenantDomain" class="text-muted ms-2 fst-italic im-feed-tenant">{{ log.tenantDomain }}</small>
                <small class="text-muted ms-auto">{{ formatApiLogTime(log.timeStamp) }}</small>
              </div>
              <small class="text-muted text-truncate">{{ truncateApiLogMessage(log.message) }}</small>
            </a>
          </div>
          <div *ngIf="!loadingEPortalLogs && recentEPortalLogs.length === 0" class="dash-card-body text-center py-3 text-muted">
            <i class="bi bi-check-circle me-1" aria-hidden="true"></i>{{ 'EPortalApiLog.noRecentLogs' | translate }}
          </div>
          <div class="dash-card-footer">
            <a routerLink="/system-admin/eportal-api-log" class="btn btn-sm btn-action-outline">
              {{ 'EPortalApiLog.viewAll' | translate }} <i class="bi bi-arrow-right ms-1" aria-hidden="true"></i>
            </a>
          </div>
        </div>

      </div>
    </div>

    <!-- Search shortcut hint -->
    <div *ngIf="!searchText" class="text-center mt-3">
      <small class="text-muted">{{ 'IntegrationMonitor.searchShortcut' | translate }}</small>
    </div>

  </ng-container>

  <!-- ═══════════════════════════════════════════════════════════════ -->
  <!-- TV MODE BODY                                                    -->
  <!-- ═══════════════════════════════════════════════════════════════ -->
  <ng-container *ngIf="monitorData && tvMode">
    <div class="im-tv-body">
      <!-- LEFT: Cards grid -->
      <div class="im-tv-cards">
        <div class="im-tv-card-grid">
          <div *ngFor="let tenant of filteredTenants; trackBy: trackByDomain" class="im-tv-card"
               [class.im-tv-card-error]="hasUnackedFailures(tenant) || tenant.connectionError"
               [class.im-tv-card-warning]="hasWarningsOnly(tenant)"
               [class.im-tv-card-ok]="!hasUnackedFailures(tenant) && !tenant.connectionError && !hasWarningsOnly(tenant) && tenant.totalIntegrations > 0"
               [class.im-tv-card-empty]="!hasUnackedFailures(tenant) && !tenant.connectionError && !hasWarningsOnly(tenant) && tenant.totalIntegrations === 0"
               [class.im-tv-pulse]="isErrorNew(tenant.domainID, null, !!tenant.connectionError)">
            <div class="im-tv-card-header">
              <span class="im-tv-card-name">{{ tenant.domainName || tenant.databaseName }}</span>
              <span class="im-tv-card-count">{{ tenant.activeIntegrations }}/{{ tenant.totalIntegrations }}</span>
            </div>
            <div class="im-tv-card-body">
              <div *ngIf="tenant.connectionError" class="im-tv-line im-tv-error">
                <i class="bi bi-database-x me-1" aria-hidden="true"></i>{{ 'IntegrationMonitor.connectionError' | translate }}
              </div>
              <div *ngIf="getFailedIntegrations(tenant).length > 0" class="im-tv-line im-tv-error">
                <i class="bi bi-x-circle me-1" aria-hidden="true"></i>
                <span *ngFor="let item of getFailedIntegrations(tenant); let last = last">{{ item.integrationName }}<span *ngIf="!last">, </span></span>
                <span *ngIf="tenant.failedLast24h > 0" class="im-tv-fail-badge ms-2">{{ tenant.failedLast24h }}</span>
              </div>
              <div *ngIf="!tenant.connectionError && getOkIntegrations(tenant).length > 0" class="im-tv-line text-success">
                <i class="bi bi-check-circle me-1" aria-hidden="true"></i>{{ getOkIntegrations(tenant).length }} OK
                <span class="im-tv-time">({{ getLastOkRunTime(tenant) }})</span>
              </div>
              <div *ngIf="!tenant.connectionError && tenant.totalIntegrations === 0" class="im-tv-line im-tv-dim">
                {{ 'IntegrationMonitor.noIntegrations' | translate }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- RIGHT: Live feed -->
      <div class="im-tv-feed">
        <div class="im-tv-feed-panel">
          <div class="im-tv-feed-header">
            <i class="bi bi-lightning-charge-fill me-2" aria-hidden="true"></i>{{ 'IntegrationMonitor.recentErrors' | translate }}
            <span class="im-tv-feed-count ms-auto">{{ getRecentErrors().length }}</span>
          </div>
          <div class="im-tv-feed-list">
            <div *ngIf="getRecentErrors().length === 0" class="im-tv-feed-empty">
              <i class="bi bi-check-circle-fill me-2 text-success" aria-hidden="true"></i>{{ 'IntegrationMonitor.noErrors' | translate }}
            </div>
            <div *ngFor="let err of getRecentErrors(); trackBy: trackByErrorKey" class="im-tv-feed-item"
                 [class.im-tv-pulse]="isErrorNew(err.domainId, err.integrationId, err.isConnection)">
              <div class="im-tv-feed-tenant-row">
                <span class="im-tv-feed-tenant">{{ err.tenantName }}</span>
                <span *ngIf="err.alertLevel" class="badge ms-2" [ngClass]="getSeverityClass(err.alertLevel)">{{ err.alertLevel }}</span>
                <span class="im-tv-feed-time ms-auto" *ngIf="err.timeSince">{{ err.timeSince }}</span>
              </div>
              <div class="im-tv-feed-name">
                <i class="bi" [class.bi-database-x]="err.isConnection" [class.bi-x-circle]="!err.isConnection" aria-hidden="true"></i>
                {{ err.integrationName }}
                <small class="im-tv-feed-type">{{ err.typeName }}</small>
              </div>
              <div *ngIf="err.errorMessage" class="im-tv-feed-msg" [title]="err.errorMessage">{{ err.errorMessage }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </ng-container>

  <!-- ═══════════════════════════════════════════════════════════════ -->
  <!-- TENANT CARD TEMPLATE                                            -->
  <!-- ═══════════════════════════════════════════════════════════════ -->
  <ng-template #tenantCardTpl let-tenant>
    <div class="im-card"
         [class.im-card-error]="hasUnackedFailures(tenant) || tenant.connectionError"
         [class.im-card-warning]="hasWarningsOnly(tenant)"
         [class.im-card-ok]="!hasUnackedFailures(tenant) && !tenant.connectionError && !hasWarningsOnly(tenant) && tenant.totalIntegrations > 0"
         [class.im-card-empty]="!hasUnackedFailures(tenant) && !tenant.connectionError && !hasWarningsOnly(tenant) && tenant.totalIntegrations === 0"
         [class.im-card-pulse]="isErrorNew(tenant.domainID, null, !!tenant.connectionError)">

      <button type="button" class="im-card-header" (click)="toggleTenant(tenant.domainID)"
              [attr.aria-expanded]="isTenantExpanded(tenant.domainID)"
              [attr.aria-label]="(tenant.domainName || tenant.databaseName) + ' — ' + (isTenantExpanded(tenant.domainID) ? 'lukk' : 'åpne')">
        <span class="im-card-dot"
              [class.im-dot-danger]="hasUnackedFailures(tenant) || tenant.connectionError"
              [class.im-dot-warning]="hasWarningsOnly(tenant)"
              [class.im-dot-success]="!hasUnackedFailures(tenant) && !tenant.connectionError && !hasWarningsOnly(tenant) && tenant.totalIntegrations > 0"
              [class.im-dot-muted]="!hasUnackedFailures(tenant) && !tenant.connectionError && tenant.totalIntegrations === 0">
        </span>
        <strong class="im-card-name">{{ tenant.domainName || tenant.databaseName }}</strong>
        <span class="im-card-count ms-auto">{{ tenant.activeIntegrations }}/{{ tenant.totalIntegrations }}</span>
        <i class="bi im-card-chev" aria-hidden="true"
           [class.bi-chevron-down]="isTenantExpanded(tenant.domainID)"
           [class.bi-chevron-right]="!isTenantExpanded(tenant.domainID)"></i>
      </button>

      <!-- Card body — quick summary + chips -->
      <div class="im-card-body">
        <div *ngIf="tenant.connectionError" class="im-card-line text-danger">
          <i class="bi bi-database-x me-1" aria-hidden="true"></i>{{ 'IntegrationMonitor.connectionError' | translate }}
        </div>
        <div *ngIf="getFailedIntegrations(tenant).length > 0" class="im-card-line text-danger">
          <i class="bi bi-x-circle me-1" aria-hidden="true"></i>
          <span class="im-card-failed-names">{{ getFailedIntegrationNames(tenant) }}</span>
          <span *ngIf="tenant.failedLast24h > 0" class="badge badge-soft-danger ms-2">{{ tenant.failedLast24h }}</span>
        </div>
        <div *ngIf="!tenant.connectionError && getOkIntegrations(tenant).length > 0" class="im-card-line text-success">
          <i class="bi bi-check-circle me-1" aria-hidden="true"></i>{{ getOkIntegrations(tenant).length }} OK
          <small class="text-muted ms-1">({{ getLastOkRunTime(tenant) }})</small>
          <span *ngIf="getTenantTotalRecords(tenant) > 0" class="text-muted ms-auto im-card-records">
            <i class="bi bi-database" aria-hidden="true"></i> {{ getTenantTotalRecords(tenant) }}
          </span>
        </div>
        <div *ngIf="!tenant.connectionError && tenant.totalIntegrations === 0" class="im-card-line text-muted fst-italic">
          {{ 'IntegrationMonitor.noIntegrations' | translate }}
        </div>
        <div *ngIf="!tenant.connectionError && tenant.integrations.length > 0" class="im-card-chips">
          <span *ngFor="let item of tenant.integrations; trackBy: trackByIntegration"
                class="im-chip"
                [class.im-chip-ok]="item.healthStatus === 'OK'"
                [class.im-chip-failed]="item.healthStatus === 'Failed' && !item.isAcked"
                [class.im-chip-warning]="item.healthStatus === 'Warning' && !item.isAcked"
                [class.im-chip-neverrun]="item.healthStatus === 'NeverRun'"
                [class.im-chip-inactive]="item.healthStatus === 'Inactive'"
                [class.im-chip-running]="item.healthStatus === 'Running'"
                [class.im-chip-acked]="item.isAcked"
                [title]="getChipTooltip(item)">
            <i *ngIf="item.isAcked" class="bi bi-bell-slash im-chip-mute-icon" aria-hidden="true"></i>
            {{ item.integrationName }}
            <small *ngIf="item.lastExecutedDate" class="im-chip-time">{{ getTimeSince(item.lastExecutedDate) }}</small>
          </span>
        </div>
      </div>

      <!-- Expanded panel -->
      <div *ngIf="isTenantExpanded(tenant.domainID)" class="im-card-expand">
        <div *ngIf="tenant.connectionError" class="px-3 py-2">
          <small class="text-danger"><i class="bi bi-database-x me-1" aria-hidden="true"></i>{{ tenant.connectionError }}</small>
          <div class="mt-2 d-flex gap-2">
            <button type="button" class="btn btn-sm btn-outline-secondary" (click)="openInLogs(tenant.domainName)">
              <i class="bi bi-journal-code me-1" aria-hidden="true"></i>{{ 'IntegrationMonitor.actionsOpenLogs' | translate }}
            </button>
            <button type="button" class="btn btn-sm btn-outline-secondary" (click)="openInEPortalLogs(tenant.domainName)">
              <i class="bi bi-journal-text me-1" aria-hidden="true"></i>{{ 'IntegrationMonitor.actionsOpenEPortalLogs' | translate }}
            </button>
          </div>
        </div>

        <!-- Failed/Warning integrations -->
        <ng-container *ngIf="!tenant.connectionError">
          <ng-container *ngFor="let item of getFailedIntegrations(tenant); trackBy: trackByIntegration">
            <ng-container *ngTemplateOutlet="integrationRowTpl; context: { tenant: tenant, item: item }"></ng-container>
          </ng-container>

          <!-- OK integrations -->
          <ng-container *ngFor="let item of getOkIntegrations(tenant); trackBy: trackByIntegration">
            <ng-container *ngTemplateOutlet="integrationRowTpl; context: { tenant: tenant, item: item }"></ng-container>
          </ng-container>

          <!-- Muted integrations (only if showAcked) -->
          <ng-container *ngIf="showAcked">
            <ng-container *ngFor="let item of getMutedIntegrations(tenant); trackBy: trackByIntegration">
              <ng-container *ngTemplateOutlet="integrationRowTpl; context: { tenant: tenant, item: item }"></ng-container>
            </ng-container>
          </ng-container>
        </ng-container>

        <!-- No integrations -->
        <div *ngIf="!tenant.connectionError && tenant.integrations.length === 0" class="px-3 py-2">
          <small class="text-muted">{{ 'IntegrationMonitor.noIntegrations' | translate }}</small>
        </div>
      </div>
    </div>
  </ng-template>

  <!-- ═══════════════════════════════════════════════════════════════ -->
  <!-- INTEGRATION ROW TEMPLATE (used in expanded panel + grouped view)-->
  <!-- ═══════════════════════════════════════════════════════════════ -->
  <ng-template #integrationRowTpl let-tenant="tenant" let-item="item">
    <div class="im-row"
         [class.im-row-failed]="item.healthStatus === 'Failed' && !item.isAcked"
         [class.im-row-warning]="item.healthStatus === 'Warning' && !item.isAcked"
         [class.im-row-acked]="item.isAcked">
      <div class="im-row-main">
        <i class="bi im-row-icon" aria-hidden="true"
           [class.bi-x-circle-fill]="item.healthStatus === 'Failed'"
           [class.text-danger]="item.healthStatus === 'Failed' && !item.isAcked"
           [class.bi-exclamation-triangle-fill]="item.healthStatus === 'Warning'"
           [class.text-warning]="item.healthStatus === 'Warning' && !item.isAcked"
           [class.bi-check-circle-fill]="item.healthStatus === 'OK'"
           [class.text-success]="item.healthStatus === 'OK'"
           [class.bi-clock-history]="item.healthStatus === 'NeverRun'"
           [class.text-muted]="item.healthStatus === 'NeverRun' || item.isAcked"
           [class.bi-bell-slash-fill]="item.isAcked"></i>

        <div class="im-row-name-block">
          <div class="im-row-name-line">
            <strong>{{ item.integrationName }}</strong>
            <small class="text-muted ms-2 d-none d-sm-inline">{{ tenant.domainName || tenant.databaseName }}</small>
            <span *ngIf="item.alertLevel" class="badge badge-sm ms-2" [ngClass]="getSeverityClass(item.alertLevel)">{{ item.alertLevel }}</span>
            <span *ngIf="item.isAcked" class="badge badge-sm badge-soft-secondary ms-2">
              <i class="bi bi-bell-slash me-1" aria-hidden="true"></i>{{ 'IntegrationMonitor.muteUntil' | translate }} {{ formatDate(item.ackedUntil) }}
            </span>
          </div>
          <div class="im-row-meta">
            <small class="text-muted">{{ item.integrationTypeName }}</small>
            <small *ngIf="item.lastRecordsProcessed != null" class="text-muted">
              · {{ item.lastRecordsSucceeded ?? 0 }}/{{ item.lastRecordsProcessed }}
              <span *ngIf="item.lastRecordsFailed && item.lastRecordsFailed > 0" class="text-danger fw-semibold">({{ item.lastRecordsFailed }} feil)</span>
            </small>
            <small *ngIf="item.lastExecutionDurationMs != null" class="text-muted">· {{ formatDuration(item.lastExecutionDurationMs) }}</small>
            <small *ngIf="item.lastSuccessfulRunDate" class="text-muted">· {{ 'IntegrationMonitor.lastSuccess' | translate }}: {{ getTimeSince(item.lastSuccessfulRunDate) }}</small>
            <small *ngIf="item.nextScheduledDate" class="text-muted">· {{ 'IntegrationMonitor.nextRun' | translate }}: {{ formatDate(item.nextScheduledDate) }}</small>
          </div>
          <div *ngIf="item.lastErrorMessage && (item.healthStatus === 'Failed' || item.healthStatus === 'Warning') && !item.isAcked" class="im-row-error">
            <small class="text-danger" [title]="item.lastErrorMessage">
              <i class="bi bi-exclamation-circle me-1" aria-hidden="true"></i>{{ item.lastErrorMessage }}
            </small>
          </div>
        </div>

        <div class="im-row-actions">
          <small *ngIf="item.lastExecutedDate" class="text-muted im-row-time">{{ getTimeSince(item.lastExecutedDate) }}</small>

          <div ngbDropdown placement="bottom-end" class="d-inline-block">
            <button type="button" class="btn btn-sm btn-outline-secondary" ngbDropdownToggle
                    [attr.aria-label]="'IntegrationMonitor.actionsMore' | translate">
              <i class="bi bi-three-dots-vertical" aria-hidden="true"></i>
            </button>
            <div ngbDropdownMenu class="dropdown-menu-end">
              <button ngbDropdownItem type="button" (click)="openHistoryModal(tenant, item)">
                <i class="bi bi-clock-history me-2" aria-hidden="true"></i>{{ 'IntegrationMonitor.actionsViewHistory' | translate }}
              </button>
              <button ngbDropdownItem type="button" (click)="openInLogs(tenant.domainName, item.integrationName)">
                <i class="bi bi-journal-code me-2" aria-hidden="true"></i>{{ 'IntegrationMonitor.actionsOpenLogs' | translate }}
              </button>
              <button ngbDropdownItem type="button" (click)="openInEPortalLogs(tenant.domainName, item.integrationName)">
                <i class="bi bi-journal-text me-2" aria-hidden="true"></i>{{ 'IntegrationMonitor.actionsOpenEPortalLogs' | translate }}
              </button>
              <button ngbDropdownItem type="button" (click)="openTenantSite(tenant)">
                <i class="bi bi-box-arrow-up-right me-2" aria-hidden="true"></i>{{ 'IntegrationMonitor.actionsOpenTenant' | translate }}
              </button>
              <div class="dropdown-divider"></div>
              <button *ngIf="!item.isAcked" ngbDropdownItem type="button" (click)="openMuteModal(tenant, item)">
                <i class="bi bi-bell-slash me-2" aria-hidden="true"></i>{{ 'IntegrationMonitor.actionsMute' | translate }}
              </button>
              <button *ngIf="item.isAcked" ngbDropdownItem type="button" (click)="unmute(tenant, item)">
                <i class="bi bi-bell me-2" aria-hidden="true"></i>{{ 'IntegrationMonitor.actionsUnmute' | translate }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </ng-template>

  <!-- ═══════════════════════════════════════════════════════════════ -->
  <!-- HISTORY MODAL                                                   -->
  <!-- ═══════════════════════════════════════════════════════════════ -->
  <ng-template #historyModal let-modal>
    <div class="modal-header">
      <h5 class="modal-title">
        <i class="bi bi-clock-history me-2" aria-hidden="true"></i>{{ 'IntegrationMonitor.historyTitle' | translate }}
        <small class="text-muted ms-2" *ngIf="historyContext">
          {{ historyContext.tenant.domainName }} · {{ historyContext.integration.integrationName }}
        </small>
      </h5>
      <button type="button" class="btn-close" (click)="modal.dismiss()" aria-label="Close"></button>
    </div>
    <div class="modal-body">
      <div *ngIf="historyLoading" class="text-center py-3">
        <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
      </div>
      <div *ngIf="historyError" class="alert alert-danger">{{ historyError }}</div>
      <div *ngIf="!historyLoading && !historyError && historyItems.length === 0" class="text-center py-3 text-muted">
        <i class="bi bi-inbox d-block mb-2 fs-3" aria-hidden="true"></i>
        {{ 'IntegrationMonitor.historyEmpty' | translate }}
      </div>
      <div *ngIf="!historyLoading && historyItems.length > 0" class="table-responsive">
        <table class="table table-sm table-hover mb-0">
          <thead class="table-light">
            <tr>
              <th>{{ 'IntegrationMonitor.lastRun' | translate }}</th>
              <th>{{ 'IntegrationMonitor.status' | translate }}</th>
              <th class="text-end">{{ 'IntegrationMonitor.records' | translate }}</th>
              <th>{{ 'IntegrationMonitor.duration' | translate }}</th>
              <th>{{ 'IntegrationMonitor.historyTriggeredBy' | translate }}</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let h of historyItems; trackBy: trackByExecution"
                [class.table-danger]="h.status === 'Failed'"
                [class.table-warning]="h.status === 'PartialSuccess'">
              <td>
                <small>{{ formatDate(h.executionStartDate) }}</small>
                <small class="text-muted d-block">{{ getTimeSince(h.executionStartDate) }} siden</small>
              </td>
              <td>
                <span class="badge" [ngClass]="getHealthBadgeClass(h.status === 'Success' ? 'OK' : (h.status === 'Failed' ? 'Failed' : (h.status === 'PartialSuccess' ? 'Warning' : 'NeverRun')))">
                  {{ h.status }}
                </span>
                <small *ngIf="h.errorMessage" class="text-danger d-block text-truncate" [title]="h.errorMessage" style="max-width:240px">{{ h.errorMessage }}</small>
              </td>
              <td class="text-end">
                <span *ngIf="h.recordsProcessed != null">{{ h.recordsSucceeded ?? 0 }}/{{ h.recordsProcessed }}</span>
                <span *ngIf="h.recordsProcessed == null">-</span>
              </td>
              <td>{{ formatDuration(h.executionDurationMs ?? undefined) }}</td>
              <td><small class="text-muted">{{ h.triggeredBy || '-' }}</small></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="modal-footer">
      <button type="button" class="btn btn-sm btn-action-outline" *ngIf="historyContext"
              (click)="openInLogs(historyContext.tenant.domainName, historyContext.integration.integrationName); modal.close()">
        <i class="bi bi-journal-code me-1" aria-hidden="true"></i>{{ 'IntegrationMonitor.openInLogs' | translate }}
      </button>
      <button type="button" class="btn btn-sm btn-action-dark" (click)="modal.close()">
        {{ 'General.close' | translate }}
      </button>
    </div>
  </ng-template>

  <!-- ═══════════════════════════════════════════════════════════════ -->
  <!-- MUTE MODAL                                                      -->
  <!-- ═══════════════════════════════════════════════════════════════ -->
  <ng-template #muteModal let-modal>
    <div class="modal-header">
      <h5 class="modal-title">
        <i class="bi bi-bell-slash me-2" aria-hidden="true"></i>{{ 'IntegrationMonitor.actionsMute' | translate }}
        <small class="text-muted ms-2" *ngIf="muteContext">
          {{ muteContext.integration.integrationName }}
        </small>
      </h5>
      <button type="button" class="btn-close" (click)="cancelMute()" aria-label="Close"></button>
    </div>
    <div class="modal-body">
      <div class="mb-3">
        <label class="form-label form-label-sm">{{ 'IntegrationMonitor.actionsMute' | translate }}</label>
        <div class="btn-group btn-group-sm w-100" role="group">
          <input type="radio" class="btn-check" id="mute1h" name="muteH" [value]="1" [(ngModel)]="muteHours" />
          <label class="btn btn-outline-secondary" for="mute1h">{{ 'IntegrationMonitor.muteFor1h' | translate }}</label>

          <input type="radio" class="btn-check" id="mute4h" name="muteH" [value]="4" [(ngModel)]="muteHours" />
          <label class="btn btn-outline-secondary" for="mute4h">{{ 'IntegrationMonitor.muteFor4h' | translate }}</label>

          <input type="radio" class="btn-check" id="mute24h" name="muteH" [value]="24" [(ngModel)]="muteHours" />
          <label class="btn btn-outline-secondary" for="mute24h">{{ 'IntegrationMonitor.muteFor24h' | translate }}</label>

          <input type="radio" class="btn-check" id="mute72h" name="muteH" [value]="72" [(ngModel)]="muteHours" />
          <label class="btn btn-outline-secondary" for="mute72h">{{ 'IntegrationMonitor.muteFor72h' | translate }}</label>

          <input type="radio" class="btn-check" id="mute7d" name="muteH" [value]="168" [(ngModel)]="muteHours" />
          <label class="btn btn-outline-secondary" for="mute7d">{{ 'IntegrationMonitor.muteFor7d' | translate }}</label>
        </div>
      </div>
      <div class="mb-2">
        <label class="form-label form-label-sm" for="muteReasonInput">{{ 'IntegrationMonitor.muteReasonPlaceholder' | translate }}</label>
        <textarea id="muteReasonInput" class="form-control form-control-sm" rows="2" [(ngModel)]="muteReason"></textarea>
      </div>
    </div>
    <div class="modal-footer">
      <button type="button" class="btn btn-sm btn-action-outline" (click)="cancelMute()" [disabled]="muteSubmitting">
        {{ 'IntegrationMonitor.muteCancel' | translate }}
      </button>
      <button type="button" class="btn btn-sm btn-action-dark" (click)="confirmMute()" [disabled]="muteSubmitting">
        <span *ngIf="muteSubmitting" class="spinner-border spinner-border-sm me-1" role="status"></span>
        {{ 'IntegrationMonitor.muteConfirm' | translate }}
      </button>
    </div>
  </ng-template>

</div>
